/**
 * The Watchout read and the three writes (`complan-governance` R7.4, R7.8, R7.9).
 *
 * ## Why the writes invalidate the watchout list and nothing else
 *
 * R7.7: adding, editing or resolving a Watchout leaves `initiative.version`, the
 * Initiative's modified-at value and the changed-after-approval flag **unchanged**. A
 * Watchout is a concern recorded *about* a record, not a change *to* it. So these
 * mutations must not invalidate the Initiative detail query: doing so would refetch a
 * record that did not change, and - worse - would suggest to a reader that raising a
 * concern had edited the Initiative.
 *
 * The change history *is* invalidated, because R7.6 requires the Change_Event to surface
 * in that Initiative's history, and that list genuinely gained a row.
 */
import {
  watchoutListResponseSchema,
  type CreateWatchoutRequest,
  type ResolveWatchoutRequest,
  type UpdateWatchoutRequest,
  type WatchoutListResponse,
} from '@complan/contracts';
import {
  useMutation,
  useQueryClient,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiFetch } from '@/shared/api/client';

import { detailQueryKeys } from '../detail-data';

/** The query keys for one Initiative's Watchouts. */
export const watchoutQueryKeys = {
  all: ['watchouts'] as const,
  forInitiative: (initiativeId: string) => ['watchouts', initiativeId] as const,
} as const;

/** `GET /api/initiatives/:id/watchouts` (R7.9). */
export async function fetchWatchouts(initiativeId: string): Promise<WatchoutListResponse> {
  const body = await apiFetch<unknown>(`/api/initiatives/${initiativeId}/watchouts`);
  return watchoutListResponseSchema.parse(body);
}

/**
 * See {@link fetchWatchouts}.
 *
 * An unreadable Initiative answers with an empty list rather than a 403, because RLS
 * filters the rows and "no watchouts" is what R7.10 requires a reader who may not see them
 * to be told - the absence of a concern, not the existence of one they cannot read.
 */
export function useWatchouts(
  initiativeId: string,
): UseQueryResult<WatchoutListResponse, unknown> {
  return useQuery({
    queryKey: watchoutQueryKeys.forInitiative(initiativeId),
    queryFn: () => fetchWatchouts(initiativeId),
    enabled: initiativeId !== '',
  });
}

/** What every Watchout write invalidates; see this module's header for what it does not. */
function useWatchoutInvalidation(initiativeId: string): () => void {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: watchoutQueryKeys.forInitiative(initiativeId) });
    // R7.6: the Change_Event belongs to this Initiative's history, which did gain a row.
    // Keyed through the Detail_Panel's own factory rather than a literal, so the two
    // cannot drift into invalidating different caches.
    void client.invalidateQueries({ queryKey: detailQueryKeys.history(initiativeId) });
  };
}

/** `POST /api/initiatives/:id/watchouts` (R7.1, R7.4). */
export function useAddWatchout(
  initiativeId: string,
): UseMutationResult<unknown, unknown, CreateWatchoutRequest> {
  const invalidate = useWatchoutInvalidation(initiativeId);
  return useMutation({
    mutationFn: (request: CreateWatchoutRequest) =>
      apiFetch<unknown>(`/api/initiatives/${initiativeId}/watchouts`, {
        method: 'POST',
        body: JSON.stringify(request),
      }),
    onSuccess: invalidate,
  });
}

/** `PUT /api/watchouts/:id` (R7.4, R7.6). */
export function useEditWatchout(
  initiativeId: string,
): UseMutationResult<
  unknown,
  unknown,
  { readonly watchoutId: string; readonly request: UpdateWatchoutRequest }
> {
  const invalidate = useWatchoutInvalidation(initiativeId);
  return useMutation({
    mutationFn: ({ watchoutId, request }) =>
      apiFetch<unknown>(`/api/watchouts/${watchoutId}`, {
        method: 'PUT',
        body: JSON.stringify(request),
      }),
    onSuccess: invalidate,
  });
}

/**
 * `POST /api/watchouts/:id/resolve` (R7.8).
 *
 * Resolving retains the Watchout rather than deleting it, which is why this is its own
 * operation and not a delete: R7.8 requires the resolution timestamp and the resolving
 * actor to be recorded *on the retained row*, so the concern and its closure both stay
 * readable.
 */
export function useResolveWatchout(
  initiativeId: string,
): UseMutationResult<
  unknown,
  unknown,
  { readonly watchoutId: string; readonly request: ResolveWatchoutRequest }
> {
  const invalidate = useWatchoutInvalidation(initiativeId);
  return useMutation({
    mutationFn: ({ watchoutId, request }) =>
      apiFetch<unknown>(`/api/watchouts/${watchoutId}/resolve`, {
        method: 'POST',
        body: JSON.stringify(request),
      }),
    onSuccess: invalidate,
  });
}
